import { useContext, useMemo, useRef, useState } from 'react';
import { shallowEqual, useDispatch } from 'react-redux';
import { useAsyncFn } from 'react-use';

import { DetailedIconHammer, Spacer } from '@zillow/constellation';
import {
  AdornedInput,
  Anchor,
  Box,
  Button,
  Combobox,
  Form,
  FormActions,
  FormField,
  FormHelp,
  Input,
  Label,
  LoadingMask,
  Select,
  Textarea,
} from '@zillow/constellation-10';

import {
  RentalManagerPropertyAddressVerificationWebApiHandler_verification,
  stringHelper,
} from '@zg-rentals/rental-manager-env';
import { PartialMessage } from '@zg-rentals/backend-styled-components';

import { RMWContext } from '../../providers/RMWContext';

import { useSelector } from '../../selectors/useSelector';

import { useGetHelpTopics } from '../../hooks/queries/support';
import { useAddressAutocomplete } from '../../hooks/queries/address';

import SubmitRequestActions from '../../actions/SubmitRequestActions';

import { HelpTopic } from '../../model/HelpTopics';

import AddressConstants from '../../constants/AddressConstants';

import type { ChangeEvent, FormEvent } from 'react';
import type { ComboboxOptionType } from '@zillow/constellation-10';
import type { HelpCategoryProps, HelpTopicProps } from '../../model/HelpTopics';
import type SupportRequestBody from '../../model/SupportRequest';

type Props = {
  onSubmit: (params: SupportRequestBody) => void;
  queryListingAlias?: string;
  queryListingAddress?: string;
  queryUnit?: string;
  isCategoryReadOnly: boolean;
  isTopicReadOnly: boolean;
  isSubmitting: boolean;
};

const SubmitRequestForm = ({
  onSubmit,
  isCategoryReadOnly,
  isTopicReadOnly,
  queryListingAlias,
  queryListingAddress,
  queryUnit,
  isSubmitting,
}: Props) => {
  const dispatch = useDispatch();

  const { paths } = useContext(RMWContext);

  const {
    user: { firstName: userFirstName = '', lastName: userLastName = '', loggedIn: isLoggedIn, email: userEmail = '' },
    submitRequest: { selectedHelpCategory, selectedHelpTopic },
  } = useSelector((state) => {
    return { user: state.user, submitRequest: state.submitRequest };
  }, shallowEqual);

  const [listingAddress, setListingAddress] = useState(queryListingAddress || '');
  const [unit, setUnit] = useState(queryUnit || '');
  const [email, setEmail] = useState(userEmail);
  const [isEmailError, setIsEmailError] = useState(false);
  const [description, setDescription] = useState('');
  const [firstName, setFirstName] = useState(userFirstName);
  const [lastName, setLastName] = useState(userLastName);
  const [setAddress, queryData] = useAddressAutocomplete();
  const addressInputRef = useRef<HTMLInputElement>(null);

  const { data: helpTopics } = useGetHelpTopics({ isLoggedIn });

  const [addressVerificationState, verifyAddress] = useAsyncFn(async (address: string) => {
    if (address === '') {
      return;
    }

    return RentalManagerPropertyAddressVerificationWebApiHandler_verification({
      fullAddress: address,
    });
  });

  const isLoginAndAddressRequired = selectedHelpCategory?.key === 'myListing';
  const canSubmit = useMemo<boolean>(() => {
    if (isEmailError) return false;
    if (addressVerificationState.error) return false;
    if (!isLoggedIn && !stringHelper.validateEmail(email)) return false;
    if (isLoginAndAddressRequired && (listingAddress === '' || !isLoggedIn)) return false;
    return description !== '' && selectedHelpCategory?.key !== '' && selectedHelpTopic?.key !== '';
  }, [
    email,
    isLoggedIn,
    selectedHelpCategory,
    selectedHelpTopic,
    description,
    listingAddress,
    isEmailError,
    addressVerificationState.error,
  ]);

  const verifyAndUpdateEmail = (newEmail: string) => {
    setIsEmailError(newEmail !== '' ? !stringHelper.validateEmail(newEmail) : false);
    setEmail(newEmail);
  };

  const handleSelectAddress = async (option: ComboboxOptionType) => {
    const addressValue = typeof option === 'string' ? option : option.value;

    await verifyAddress(addressValue);
    setListingAddress(addressValue);
  };

  const handleAddressChange = (e: ChangeEvent<HTMLInputElement>) => {
    setListingAddress(e.target.value);

    setAddress(e.target.value);
  };

  const handleSelectCategory = (value: string) => {
    const selectedCategory = helpTopics?.categories?.find((category) => category.key === value);
    if (selectedCategory) {
      dispatch(SubmitRequestActions.updateSelectedHelpCategory(selectedCategory));
      dispatch(SubmitRequestActions.updateSelectedHelpTopic(HelpTopic({ key: '', description: '' })));
    }
  };

  const handleSelectTopic = (value: string) => {
    const selectedTopic = selectedHelpCategory?.topics?.find((topic: HelpTopicProps) => topic.key === value);
    if (selectedTopic) {
      dispatch(SubmitRequestActions.updateSelectedHelpTopic(selectedTopic));
    }
  };

  const handleSubmitRequest = () => {
    const params: SupportRequestBody = {
      category: selectedHelpCategory?.key,
      topic: selectedHelpTopic?.key,
      email,
      firstName,
      lastName,
      listingAddress,
      unit,
      description,
    };
    if (queryListingAlias) {
      params.listingAlias = queryListingAlias;
    }
    onSubmit(params);
  };
  return (
    <Form asChild={true} data-testid="SubmitRequestForm" onSubmit={(e: FormEvent) => e.preventDefault()}>
      <div>
        <LoadingMask css={{ display: 'block' }} loading={isSubmitting}>
          <FormField
            label={<Label>{isCategoryReadOnly ? 'Category' : 'I need help with'}</Label>}
            control={
              isCategoryReadOnly ? (
                <Input value={selectedHelpCategory?.description} readOnly={true} />
              ) : (
                <Select
                  aria-label="RequestType"
                  name="requestType"
                  value={selectedHelpCategory?.key}
                  onChange={(e: ChangeEvent<HTMLSelectElement>) => handleSelectCategory(e.target.value)}
                  css={{ whiteSpace: 'pre' }}
                >
                  <option disabled={selectedHelpCategory?.key !== ''} aria-label="Please select an option">
                    -
                  </option>

                  {helpTopics?.categories?.map((category: HelpCategoryProps) => {
                    return (
                      <option key={category?.key} value={category?.key} aria-label={category?.description}>
                        {category?.description}
                      </option>
                    );
                  })}
                </Select>
              )
            }
            description={
              isLoginAndAddressRequired && !isLoggedIn ? (
                <FormHelp>
                  <div>
                    Please{' '}
                    <Anchor href={paths.zrmSignInPage(`${paths.submitRequest}`) || ''}>
                      <strong>sign in</strong>
                    </Anchor>{' '}
                    to submit a request about your listing. If you are unable to sign in, select <b>My Account</b>.
                  </div>
                </FormHelp>
              ) : null
            }
            required={true}
          />
          {selectedHelpCategory?.key === '' &&
            helpTopics?.outages?.map((outage, i) => {
              return (
                <Box
                  css={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: { base: 'flex-start', lg: 'center' },
                    justifyContent: 'flex-start',
                    paddingBlock: '200',
                  }}
                  key={`outages-content-${i}`}
                >
                  <Spacer marginRight="xxs">
                    <DetailedIconHammer size="sm" />
                  </Spacer>
                  <PartialMessage partialMessages={outage} fontType="finePrint" />
                </Box>
              );
            })}
          {selectedHelpCategory?.key !== '' && (
            <>
              <FormField
                label={
                  <Label>{isTopicReadOnly ? 'Topic' : 'Select the topic below that best fits your problem'}</Label>
                }
                control={
                  isTopicReadOnly ? (
                    <Input value={selectedHelpTopic?.description} readOnly={true} />
                  ) : (
                    <Select
                      aria-label="RequestType"
                      name="requestType"
                      value={selectedHelpTopic?.key}
                      onChange={(e: ChangeEvent<HTMLSelectElement>) => handleSelectTopic(e.target.value)}
                      css={{ whiteSpace: 'pre' }}
                    >
                      <option disabled={selectedHelpTopic?.key !== ''} aria-label="Please select an option">
                        -
                      </option>

                      {selectedHelpCategory?.topics?.map((topic: HelpTopicProps) => {
                        return (
                          <option key={topic?.key} value={topic?.key} aria-label={topic?.description}>
                            {topic?.description}
                          </option>
                        );
                      })}
                    </Select>
                  )
                }
                required={true}
              />
              <Box css={{ display: 'flex', flexDirection: { base: 'column', lg: 'row' } }}>
                <FormField
                  css={{ flex: '1 1 auto', marginRight: { base: 0, lg: '400' } }}
                  label={<Label>First name</Label>}
                  control={
                    <Input
                      value={firstName}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value)}
                      className="fs-mask"
                    />
                  }
                />
                <FormField
                  css={{ flex: '1 1 auto', marginBottom: '400' }}
                  label={<Label>Last name</Label>}
                  control={
                    <Input
                      value={lastName}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => setLastName(e.target.value)}
                      className="fs-mask"
                    />
                  }
                />
              </Box>
              <Box css={{ marginBottom: isEmailError ? '0' : '400', paddingBottom: isEmailError ? '0' : '100' }}>
                <FormField
                  label={<Label>Email address</Label>}
                  control={
                    <Input
                      placeholder="Type your email address here"
                      error={isEmailError}
                      value={email}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => verifyAndUpdateEmail(e.target.value)}
                    />
                  }
                  required={!isLoggedIn}
                >
                  {isEmailError && <FormHelp error={isEmailError}>Enter one valid email address.</FormHelp>}
                </FormField>
              </Box>
              <Box css={{ display: 'flex', flexDirection: { base: 'column', lg: 'row' } }}>
                <Box
                  css={{
                    marginBottom: addressVerificationState.error ? '0' : '400',
                    paddingBottom: addressVerificationState.error ? '0' : '100',
                    flexBasis: '70%',
                  }}
                >
                  <FormField required={isLoginAndAddressRequired} css={{ marginRight: { base: 0, lg: 'sm' } }}>
                    <Label id="street-address-label">Listing address</Label>
                    <Combobox
                      appearance="input"
                      focusFirstOption={true}
                      options={
                        queryData?.data?.addresses?.map((address: { name: string }) => ({
                          ...address,
                          value: address.name,
                          label: address.name,
                        })) ?? []
                      }
                      onClear={() => handleAddressChange({ target: { value: '' } } as ChangeEvent<HTMLInputElement>)}
                      onOptionSelect={handleSelectAddress}
                      selectCallbackFrequency="always"
                      error={!!addressVerificationState.error}
                      disabled={Boolean(queryListingAddress)}
                      renderInput={() => {
                        return (
                          <Combobox.Input
                            ref={addressInputRef}
                            value={listingAddress}
                            onChange={handleAddressChange}
                            onBlur={() => verifyAddress(listingAddress)}
                            aria-labelledby="street-address-label"
                          />
                        );
                      }}
                      renderEmptyState={() => listingAddress.length <= 7 && null}
                      freeForm={true}
                      autoCompleteBehavior="manual"
                    />
                    {addressVerificationState.error && (
                      <FormHelp error={Boolean(addressVerificationState.error)}>
                        Enter a complete, valid address.
                      </FormHelp>
                    )}
                  </FormField>
                </Box>
                <Box css={{ marginBottom: '400', flexBasis: '30%' }}>
                  <FormField
                    css={{ flex: '1 1 auto' }}
                    label={<Label>Unit number</Label>}
                    control={
                      <AdornedInput
                        startAdornment={<AdornedInput.Adornment>#</AdornedInput.Adornment>}
                        input={
                          <AdornedInput.Input
                            aria-labelledby="unit-number-label"
                            readOnly={Boolean(queryUnit)}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                              if (e.target.value.length <= AddressConstants.UNIT_NUMBER_LENGTH_LIMIT) {
                                setUnit(e.target.value);
                              }
                            }}
                            value={unit || ''}
                          />
                        }
                      />
                    }
                  />
                </Box>
              </Box>
              <FormField
                label={<Label>Description</Label>}
                control={
                  <Textarea
                    value={description}
                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value)}
                    placeholder={
                      selectedHelpTopic?.descriptionFieldPlaceholder ||
                      'Describe your problem to help us better assist you.'
                    }
                  />
                }
                required={true}
              />
              <FormActions
                css={{
                  marginLeft: { base: '-16px', md: '-24px', lg: '0' },
                  lgDown: {
                    borderTop: '1px solid {colors.border.subtle}',
                    borderBottom: '1px solid {colors.border.subtle}',
                    position: 'fixed',
                    width: '100%',
                    bottom: 0,
                    padding: '400',
                    background: 'white',
                    zIndex: '2',
                  },
                }}
              >
                <Box css={{ display: 'flex', justifyContent: { base: 'flex-end', lg: 'flex-start' } }}>
                  <Button disabled={!canSubmit} tone="brand" emphasis="filled" onClick={handleSubmitRequest}>
                    Submit
                  </Button>
                </Box>
              </FormActions>
            </>
          )}
        </LoadingMask>
      </div>
    </Form>
  );
};

export default SubmitRequestForm;
